import { inject, OnDestroy, Service } from '@angular/core';

import { forkJoin, Observable, of } from 'rxjs';

import { BAAS_CUSTOM_EVENT, BaasAppState } from '@lvflanders/bar';
import { BarInitializedHook } from '@lvflanders/bar-ng';
import { BaasLayoutService, BarLayoutService } from '@lvflanders/bar-ng-ui';

import { ApiConfiguration } from '../../api/api-configuration';
import { HelpUrlDto } from '../../api/models';
import { StartschermCoreOptions } from '../../models/startscherm-core-options.model';
import { STARTSCHERM_CONSTANTS } from '../../startscherm.constants';
import { KlantinfoVoorwieDto } from './../../api/models/klantinfo-voorwie-dto';
import { HelpUrlApiService } from './../../api/services/help-url-api.service';
import { VoorwiepickerApiService } from './../../api/services/voorwiepicker-api.service';
import { StartschermV2StateService } from './startscherm-v2-state.service';

@Service({ autoProvided: false })
export class StartschermV2StartupService implements BarInitializedHook, OnDestroy {
    readonly #appState = inject(BaasAppState);
    readonly #startschermCoreOptions = inject(StartschermCoreOptions);
    readonly #apiConfiguration = inject(ApiConfiguration);
    readonly #voorwiepickerService = inject(VoorwiepickerApiService);
    readonly #stateV2Service = inject(StartschermV2StateService);
    readonly #helpUrlApiService = inject(HelpUrlApiService);
    readonly #layoutLegacy = inject(BarLayoutService, { optional: true });
    readonly #layout = inject(BaasLayoutService, { optional: true });

    #basiscontextChangedEventHandler = async () => {
        if (this.#appState.basiscontext.voorwie) {
            this.#stateV2Service.refreshVoorwiePicker();
        }
    };

    ngOnDestroy(): void {
        window.removeEventListener(BAAS_CUSTOM_EVENT.BASISCONTEXT_CHANGED, this.#basiscontextChangedEventHandler);
    }

    run() {
        console.log('StartschermV2StartupService is running...');
        // Listen for the custom event on a JavaScript object or DOM element
        window.addEventListener(BAAS_CUSTOM_EVENT.BASISCONTEXT_CHANGED, this.#basiscontextChangedEventHandler);
        const rootUrl = this.#defineRootUrl();
        this.#apiConfiguration.rootUrl = rootUrl;
        this.#stateV2Service.rootUrl = rootUrl;
        return this.initAllData();
    }
    getAantalKlanten(): Observable<number> {
        if (this.#stateV2Service.isExtern) {
            return this.#voorwiepickerService.getAantalVoorwies();
        } else {
            return of(undefined);
        }
    }

    getKlantInfoVoorwie(): Observable<KlantinfoVoorwieDto> {
        if (this.#appState.basiscontext.voorwie) {
            return this.#voorwiepickerService.getKlantinfoVoorwie();
        }
        return of(undefined);
    }

    getHelpUrl() {
        return forkJoin({
            defaultUrls: this.#helpUrlApiService.getDefaultHelpUrl({ 'lvflanders.bar.ng.block': false } as any),
            dbHelpUrl: this.#helpUrlApiService.getHelpUrlByUrl({ url: location.href })
        });
    }

    initAllData(): Observable<void> {
        // use bar layout http params to mark it as non-blocking
        const getHelpUrl = this.getHelpUrl();
        const getKlantInfoVoorwie = this.getKlantInfoVoorwie();
        const getAantalKlanten = this.getAantalKlanten();
        this.#setInterception(false);
        return new Observable<void>((subscriber) => {
            forkJoin({
                getHelpUrl: getHelpUrl,
                getKlantInfoVoorwie: getKlantInfoVoorwie,
                getAantalKlanten: getAantalKlanten
            }).subscribe({
                next: (res) => {
                    this.#setInterception(true);

                    if (res.getAantalKlanten) {
                        this.#stateV2Service.aantalVoorwies = res.getAantalKlanten as number;
                    }
                    if (res.getKlantInfoVoorwie) {
                        this.#stateV2Service.klantnummer = (res.getKlantInfoVoorwie as KlantinfoVoorwieDto).klantnummer;
                        this.#stateV2Service.klantnaam = (res.getKlantInfoVoorwie as KlantinfoVoorwieDto).klantnaam;
                        this.#stateV2Service.klantadres = (res.getKlantInfoVoorwie as KlantinfoVoorwieDto).klantadres;
                        this.#stateV2Service.identificatienummer = (res.getKlantInfoVoorwie as KlantinfoVoorwieDto).identificatienummer;
                        this.#stateV2Service.omschrijvingIdentificatienummer = (res.getKlantInfoVoorwie as KlantinfoVoorwieDto).omschrijvingIdentificatienummer;
                    }
                    this.handleHelpUrls(res.getHelpUrl);

                    subscriber.next();
                    subscriber.complete();
                },
                error: (error) => {
                    this.#setInterception(true);
                    subscriber.next();
                    subscriber.complete();
                }
            });
        });
    }

    handleHelpUrls(getHelpUrl: { defaultUrls: HelpUrlDto; dbHelpUrl: HelpUrlDto }) {
        const urlToSet = this.#stateV2Service.isIntern
            ? getHelpUrl.dbHelpUrl.urlIntern || getHelpUrl.defaultUrls.urlIntern
            : getHelpUrl.dbHelpUrl.urlExtern || getHelpUrl.defaultUrls.urlExtern;
        this.#stateV2Service.helpLink.set(urlToSet);
    }

    #defineRootUrl(): string {
        console.log('Defining root URL for Startscherm API calls...');
        const url =
            location.hostname === 'localhost'
                ? this.#startschermCoreOptions?.isLocal
                    ? 'https://' + location.hostname + ':44397'
                    : this.#appState.location.release.origin + STARTSCHERM_CONSTANTS.STARTSCHERM_URL_SEGMENT
                : location.origin + STARTSCHERM_CONSTANTS.STARTSCHERM_URL_SEGMENT;
        console.log('Root URL for Startscherm API calls defined as: ' + url);
        return url;
    }
    #setInterception(interception: boolean) {
        if (this.#layoutLegacy) this.#layoutLegacy.interception$.next(interception);
        if (this.#layout) this.#layout.interception = interception;
    }
}
